import { HardDrive, ArrowRight } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import type { SubscriptionTier } from '@maker-toolchest/shared';
import { TIER_LIMITS } from '@maker-toolchest/shared';

interface AddStorageModalProps {
  open: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
  currentTier: SubscriptionTier;
  nextTier: SubscriptionTier | null;
}

const TIER_NAMES: Record<SubscriptionTier, string> = {
  TINKERER: 'Tinkerer',
  HOBBYIST: 'Hobbyist',
  BUSINESS: 'Business',
};

const TIER_PRICES: Record<SubscriptionTier, string> = {
  TINKERER: 'Free',
  HOBBYIST: '$12/month',
  BUSINESS: '$49/month',
};

export function AddStorageModal({
  open,
  onClose,
  onUpgrade,
  currentTier,
  nextTier,
}: AddStorageModalProps) {
  const currentLimits = TIER_LIMITS[currentTier];
  const nextLimits = nextTier ? TIER_LIMITS[nextTier] : null;

  const formatStorage = (storageMB: number): string => {
    if (storageMB === Infinity) return 'Unlimited';
    if (storageMB >= 1024) return `${storageMB / 1024} GB`;
    return `${storageMB} MB`;
  };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <h2 className="text-lg font-semibold text-foreground">Add Storage</h2>
          <p className="mt-1 text-sm text-foreground-muted">
            Choose how you'd like to increase your storage capacity
          </p>
        </div>

        {/* Options */}
        <div className="space-y-3">
          {/* Option 1: Upgrade to next tier */}
          {nextTier && nextLimits && (
            <div className="rounded-lg border-2 border-primary bg-primary/5 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <HardDrive className="h-5 w-5 text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">
                      Upgrade to {TIER_NAMES[nextTier]}
                    </h3>
                  </div>
                  <p className="text-sm text-foreground-muted mb-3">
                    Get {formatStorage(nextLimits.storageMB)} of storage plus all {TIER_NAMES[nextTier]} features
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-primary">
                      {TIER_PRICES[nextTier]}
                    </span>
                    <span className="text-xs text-foreground-muted line-through">
                      {TIER_PRICES[currentTier]}
                    </span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <div className="text-xs text-foreground-muted space-y-1">
                      <div className="flex items-center justify-between">
                        <span>Storage</span>
                        <span className="font-medium text-foreground">
                          {formatStorage(currentLimits.storageMB)} → {formatStorage(nextLimits.storageMB)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Printers</span>
                        <span className="font-medium text-foreground">
                          {currentLimits.printers === Infinity ? 'Unlimited' : currentLimits.printers} →{' '}
                          {nextLimits.printers === Infinity ? 'Unlimited' : nextLimits.printers}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Filaments</span>
                        <span className="font-medium text-foreground">
                          {currentLimits.filaments === Infinity ? 'Unlimited' : currentLimits.filaments} →{' '}
                          {nextLimits.filaments === Infinity ? 'Unlimited' : nextLimits.filaments}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {onUpgrade && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      onUpgrade();
                      onClose();
                    }}
                    rightIcon={<ArrowRight className="h-4 w-4" />}
                  >
                    Upgrade
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Option 2: Add storage blocks */}
          <div className="rounded-lg border border-border bg-background-subtle p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <HardDrive className="h-5 w-5 text-foreground-muted" />
                  <h3 className="text-sm font-semibold text-foreground">Add Storage Blocks</h3>
                </div>
                <p className="text-sm text-foreground-muted mb-3">
                  Add 5 GB storage blocks to your current plan
                </p>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-semibold text-foreground">$2/month</span>
                  <span className="text-xs text-foreground-muted">per 5 GB block</span>
                </div>
                <div className="mt-3 pt-3 border-t border-border/50">
                  <p className="text-xs text-foreground-muted">
                    Keep your {TIER_NAMES[currentTier]} plan and only add the storage you need
                  </p>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                disabled
                title="Coming soon"
              >
                Coming Soon
              </Button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
